import React, { useState, useEffect } from "react";
import { LOGO_URL, COMPANY } from "@/lib/site-data";
import { HOME } from "@/constants/testIds";
import { Phone, List, X } from "@phosphor-icons/react";

const NavLink = ({ href, label, testId, onClick }) => (
  <a
    data-testid={testId}
    href={href}
    onClick={onClick}
    className="text-sm font-bold uppercase tracking-[0.18em] text-slate-700 hover:text-sky-600 transition-colors"
  >
    {label}
  </a>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      className={`sticky top-0 z-50 w-full bg-white transition-shadow ${
        scrolled ? "shadow-[0_1px_0_0_rgba(15,23,42,0.08)]" : ""
      }`}
    >
      <div className="border-b-2 border-slate-900">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 flex items-center justify-between h-20">
          <a href="#top" className="flex items-center gap-3" data-testid={HOME.headerLogo}>
            <img src={LOGO_URL} alt="Troya Mini Bins logo" className="h-12 w-12 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-black text-base text-slate-900">TROYA <span className="text-sky-500">MINI BINS</span></div>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-500">Quality You Can Trust</div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            <NavLink href="#sizes" label="Sizes" testId={HOME.navSizes} />
            <NavLink href="#pricing" label="Pricing" testId={HOME.navPricing} />
            <NavLink href="#service-area" label="Service Area" testId={HOME.navServices} />
            <NavLink href="#faq" label="FAQ" testId={HOME.navFaq} />
            <NavLink href="#contact" label="Contact" testId={HOME.navContact} />
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              data-testid={HOME.ctaHeaderCall}
              href={`tel:${COMPANY.phoneRaw}`}
              className="flex items-center gap-2 px-4 py-3 border-2 border-slate-900 text-slate-900 font-bold text-sm hover:bg-slate-900 hover:text-white transition-colors"
            >
              <Phone size={18} weight="bold" />
              <span>{COMPANY.phone}</span>
            </a>
            <a
              data-testid={HOME.ctaHeaderQuote}
              href="/book"
              className="px-5 py-3 bg-sky-500 text-white font-bold text-sm uppercase tracking-wider hover:bg-sky-600 transition-colors border-2 border-sky-500 hover:border-sky-600"
            >
              Book Now
            </a>
          </div>

          <button
            className="lg:hidden p-2 text-slate-900"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} weight="bold" /> : <List size={26} weight="bold" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-b-2 border-slate-900">
          <div className="px-6 py-6 flex flex-col gap-5">
            <NavLink href="#sizes" label="Sizes" testId={HOME.navSizes + "-m"} onClick={close} />
            <NavLink href="#pricing" label="Pricing" testId={HOME.navPricing + "-m"} onClick={close} />
            <NavLink href="#service-area" label="Service Area" testId={HOME.navServices + "-m"} onClick={close} />
            <NavLink href="#faq" label="FAQ" testId={HOME.navFaq + "-m"} onClick={close} />
            <NavLink href="#contact" label="Contact" testId={HOME.navContact + "-m"} onClick={close} />
            <div className="flex flex-col gap-3 pt-2">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-slate-900 text-slate-900 font-bold text-sm"
                onClick={close}
              >
                <Phone size={18} weight="bold" /> {COMPANY.phone}
              </a>
              <a
                href="/book"
                className="text-center px-5 py-3 bg-sky-500 text-white font-bold text-sm uppercase tracking-wider"
                onClick={close}
              >
                Book Now
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
